import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  BodyText,
  Badge,
  Button,
  Card,
  DataGrid,
  Dialog,
  FilterBar,
  MetricCard,
  METRIC_READ_CAPTIONS,
  PageHeader,
  Skeleton,
} from '@aaimplatform/ui';
import type { ActiveFilter, ColumnDef, FilterDefinition } from '@aaimplatform/ui';
import {
  RefreshCw as RefreshIcon,
  Eye as ViewIcon,
  Home as PropertyIcon,
  AlertTriangle as WarningIcon,
  Plus as PlusIcon,
} from 'lucide-react';
import { usePropertyValuations, useCreatePropertyValuation } from '../hooks';
import { useTenantNames } from '../hooks/useTenantNames';
import type { TenantNameState } from '../hooks/useTenantNames';
import type { PropertyValuation, ValuationStatus } from '../api/client';
import {
  ABSENT,
  formatCount,
  formatDate,
  formatDateTime,
  formatMoney,
  formatMoneyCompact,
  formatRatioAsPercent,
} from '../utils/display-format';
import {
  CollateralListEmptyState,
  TenantLoanCell,
  VALUATION_STATUS_LABEL,
  ValuationStatusBadge,
  isMeasured,
  labelOptions,
  tenantDisplayName,
} from '../lib/collateral-display';
import type { CollateralEmptyCopyConfig } from '../lib/collateral-display';

/** The spec's maximum page. The counts below are taken over the rows read. */
const LIST_LIMIT = 100;

export const VALUATION_FILTER_DEFINITIONS: FilterDefinition[] = [
  {
    id: 'status',
    label: 'Status',
    type: 'select',
    options: labelOptions(VALUATION_STATUS_LABEL),
  },
];

export const VALUATION_EMPTY_COPY: CollateralEmptyCopyConfig = {
  failedTitle: 'Property valuations could not be read',
  filteredTitle: 'No valuations match the filters',
  emptyTitle: 'No property valuations recorded',
  emptyReason: 'No valuation has been requested yet. New Valuation requests one from the Reference Model.',
};

/** A valuation's figure, or absent when the record carries none or a non-positive one. */
function certifiedFigure(valuation: PropertyValuation): number | undefined {
  return isMeasured(valuation.certifiedValue) && valuation.certifiedValue > 0
    ? valuation.certifiedValue
    : undefined;
}

export interface ValuationSummaryState {
  rows: number;
  byStatus: Record<ValuationStatus, number>;
  /** Sum of the certified figure over CERTIFIED rows that carry one. */
  certifiedValue: number;
  /** CERTIFIED rows the sum could not include because they carry no figure. */
  certifiedWithoutFigure: number;
}

/**
 * Counts per status and the certified value, over the rows read. The value
 * card sums CERTIFIED rows only and says so in its label: it read "Total
 * Value" and disagreed with the table it headed (op-g2-12). A certified row
 * with no figure is counted apart rather than added as zero.
 */
export function summarizeValuations(valuations: PropertyValuation[]): ValuationSummaryState {
  const byStatus: Record<ValuationStatus, number> = {
    CERTIFIED: 0,
    PENDING: 0,
    NEEDS_REVIEW: 0,
    FAILED: 0,
  };
  let certifiedValue = 0;
  let certifiedWithoutFigure = 0;
  for (const valuation of valuations) {
    if (Object.prototype.hasOwnProperty.call(byStatus, valuation.status)) {
      byStatus[valuation.status] += 1;
    }
    if (valuation.status === 'CERTIFIED') {
      const figure = certifiedFigure(valuation);
      if (figure === undefined) certifiedWithoutFigure += 1;
      else certifiedValue += figure;
    }
  }
  return { rows: valuations.length, byStatus, certifiedValue, certifiedWithoutFigure };
}

function confidenceColor(score: number): string {
  if (score >= 0.85) return 'text-growth';
  if (score >= 0.7) return 'text-warning';
  return 'text-error';
}

function addressLine(valuation: PropertyValuation): string {
  const a = valuation.address;
  return [a?.city, [a?.state, a?.zipCode].filter(Boolean).join(' ')].filter(Boolean).join(', ');
}

function useColumns(
  names: ReadonlyMap<string, TenantNameState>,
  onView: (valuation: PropertyValuation) => void,
): ColumnDef<PropertyValuation, unknown>[] {
  return useMemo<ColumnDef<PropertyValuation, unknown>[]>(
    () => [
      {
        id: 'property',
        header: 'Property',
        accessorFn: (row) => row.address?.street ?? '',
        cell: ({ row }) => (
          <div>
            <div className="text-sm font-sans font-medium text-primary leading-normal">
              {row.original.address?.street ?? ABSENT}
            </div>
            <div className="text-xs font-sans text-tertiary leading-normal">
              {addressLine(row.original)}
            </div>
          </div>
        ),
        size: 240,
      },
      {
        id: 'tenant',
        header: 'Tenant',
        accessorFn: (row) => tenantDisplayName(row.tenantId, names),
        cell: ({ row }) => (
          <TenantLoanCell tenantId={row.original.tenantId} loanId={row.original.loanId} names={names} />
        ),
        size: 200,
      },
      {
        // Figures right-align under a right-aligned header (op-g2-5).
        id: 'value',
        header: 'Value',
        meta: { align: 'right' },
        accessorFn: (row) => certifiedFigure(row) ?? -1,
        cell: ({ row }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {formatMoney(certifiedFigure(row.original))}
          </span>
        ),
        size: 150,
      },
      {
        id: 'confidence',
        header: 'Confidence',
        meta: { align: 'right' },
        accessorFn: (row) => row.confidenceScore ?? -1,
        cell: ({ row }) => {
          const score = row.original.confidenceScore;
          return isMeasured(score) && score > 0 ? (
            <span className={`text-sm font-mono tabular-nums ${confidenceColor(score)}`}>
              {formatRatioAsPercent(score)}
            </span>
          ) : (
            <span className="text-sm font-sans text-tertiary">{ABSENT}</span>
          );
        },
        size: 110,
      },
      {
        id: 'sources',
        header: 'Sources',
        accessorFn: (row) => (row.sources ?? []).join(', '),
        cell: ({ row }) =>
          row.original.sources && row.original.sources.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {row.original.sources.map((source) => (
                <Badge key={source} variant="neutral" size="small">
                  {source}
                </Badge>
              ))}
            </div>
          ) : (
            <span className="text-xs font-sans text-tertiary">No sources</span>
          ),
        size: 120,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ getValue }) => <ValuationStatusBadge status={getValue() as string} />,
        size: 130,
      },
      {
        accessorKey: 'valuationDate',
        header: 'Date',
        cell: ({ getValue }) => (
          <span className="text-sm font-sans text-secondary leading-normal">
            {formatDate(getValue() as string | undefined)}
          </span>
        ),
        size: 120,
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onView(row.original);
            }}
            className="p-1 hover:bg-recessed rounded transition-colors"
            aria-label={`View valuation details for ${row.original.address?.street ?? row.original.valuationId}`}
            title="View details"
          >
            <ViewIcon size={20} className="text-secondary" />
          </button>
        ),
        size: 80,
      },
    ],
    [names, onView],
  );
}

const INPUT_CLASS =
  'w-full border border-recessed rounded px-3 py-2 text-sm bg-transparent outline-none focus:border-accent';

export function PropertyValuationsPage() {
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedValuation, setSelectedValuation] = useState<PropertyValuation | null>(null);
  const [revalueError, setRevalueError] = useState<string | null>(null);

  // New valuation dialog state
  const [newValuationDialogOpen, setNewValuationDialogOpen] = useState(false);
  const [newValuationForm, setNewValuationForm] = useState({
    street: '',
    city: '',
    state: '',
    zipCode: '',
    propertyType: '',
  });
  const [newValuationError, setNewValuationError] = useState<string | null>(null);

  // One unfiltered read. Filtering the read by status made every card for any
  // other status print a measured 0 while a status filter was applied.
  const { data: valuationsData, isLoading, error, refetch, isFetching } = usePropertyValuations({
    limit: LIST_LIMIT,
  });
  const createValuation = useCreatePropertyValuation();

  const valuations = useMemo(() => valuationsData?.valuations ?? [], [valuationsData]);
  const tenantNames = useTenantNames(valuations.map((v) => v.tenantId));
  const summary = useMemo(() => summarizeValuations(valuations), [valuations]);
  const total = valuationsData?.total ?? valuations.length;
  const partialRead = total > valuations.length;

  const statusFilter = activeFilters.find((f) => f.filterId === 'status')?.value;

  const filtered = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return valuations.filter((v) => {
      if (statusFilter && v.status !== statusFilter) return false;
      if (!query) return true;
      const haystack = [
        v.address?.street,
        v.address?.city,
        v.address?.state,
        v.address?.zipCode,
        v.loanId,
        v.tenantId ? tenantDisplayName(v.tenantId, tenantNames) : undefined,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [valuations, statusFilter, searchQuery, tenantNames]);

  const handleView = useCallback((valuation: PropertyValuation) => {
    setRevalueError(null);
    setSelectedValuation(valuation);
  }, []);
  const columns = useColumns(tenantNames, handleView);

  const handleOpenNewValuation = () => {
    setNewValuationForm({ street: '', city: '', state: '', zipCode: '', propertyType: '' });
    setNewValuationError(null);
    setNewValuationDialogOpen(true);
  };

  const handleSubmitNewValuation = async () => {
    setNewValuationError(null);

    const { street, city, state, zipCode } = newValuationForm;
    if (!street.trim() || !city.trim() || !state.trim() || !zipCode.trim()) {
      setNewValuationError('Street, city, state, and ZIP code are required.');
      return;
    }

    try {
      await createValuation.mutateAsync({
        address: { street: street.trim(), city: city.trim(), state: state.trim(), zipCode: zipCode.trim() },
        propertyType: newValuationForm.propertyType.trim() || undefined,
      });
      setNewValuationDialogOpen(false);
      refetch();
    } catch (err) {
      setNewValuationError(err instanceof Error ? err.message : 'Valuation request failed.');
    }
  };

  /**
   * Requests a fresh valuation for the address the row was requested with.
   * platform-admin-api keys a valuation on a hash of the requested address, so
   * the result replaces this row rather than adding a second one.
   */
  const handleRevalue = async (valuation: PropertyValuation) => {
    setRevalueError(null);
    const address = valuation.address;
    if (!address?.street || !address.city || !address.state || !address.zipCode) {
      setRevalueError('This valuation has no complete address to request again.');
      return;
    }
    try {
      await createValuation.mutateAsync({
        address: {
          street: address.street,
          city: address.city,
          state: address.state,
          zipCode: address.zipCode,
        },
        propertyType: valuation.propertyType,
        tenantId: valuation.tenantId ?? undefined,
        loanId: valuation.loanId ?? undefined,
      });
      setSelectedValuation(null);
      refetch();
    } catch (err) {
      setRevalueError(err instanceof Error ? err.message : 'Valuation request failed.');
    }
  };

  const countNote = partialRead
    ? `Of the ${formatCount(valuations.length)} most recent of ${formatCount(total)}`
    : undefined;

  const certifiedValueNote =
    summary.certifiedWithoutFigure > 0
      ? `Excludes ${formatCount(summary.certifiedWithoutFigure)} certified without a figure`
      : `Across ${formatCount(summary.byStatus.CERTIFIED)} certified`;

  return (
    <div>
      <PageHeader
        description="Property valuation management via Reference Model"
        actions={
          <>
            <Button
              variant="secondary"
              icon={RefreshIcon}
              iconPosition="left"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              Refresh
            </Button>
            <Button variant="primary" icon={PlusIcon} iconPosition="left" onClick={handleOpenNewValuation}>
              New Valuation
            </Button>
          </>
        }
      />

      {error && (
        <Alert type="error" title="Property valuations could not be read" className="mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <span>{error.message}</span>
            <Button variant="secondary" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </Alert>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="bg-surface p-6 rounded-sm shadow-sm">
              <Skeleton className="h-3 w-24 mb-3" />
              <Skeleton className="h-8 w-16" />
            </div>
          ))
        ) : error ? (
          <>
            <MetricCard label="Total Properties" value={ABSENT} caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Certified" value={ABSENT} caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Pending" value={ABSENT} caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Needs Review" value={ABSENT} caption={METRIC_READ_CAPTIONS.failed} />
            <MetricCard label="Certified Value" value={ABSENT} caption={METRIC_READ_CAPTIONS.failed} />
          </>
        ) : (
          <>
            <MetricCard label="Total Properties" value={formatCount(total)} />
            <MetricCard
              label="Certified"
              value={formatCount(summary.byStatus.CERTIFIED)}
              description={countNote}
            />
            <MetricCard
              label="Pending"
              value={formatCount(summary.byStatus.PENDING)}
              description={countNote}
            />
            <MetricCard
              label="Needs Review"
              value={formatCount(summary.byStatus.NEEDS_REVIEW)}
              description={countNote}
            />
            <MetricCard
              label="Certified Value"
              value={formatMoneyCompact(summary.certifiedValue)}
              description={certifiedValueNote}
            />
          </>
        )}
      </div>

      <FilterBar
        filters={VALUATION_FILTER_DEFINITIONS}
        activeFilters={activeFilters}
        onFiltersChange={setActiveFilters}
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search by address, loan ID, or tenant..."
        className="rounded-t-sm"
      />

      {isLoading ? (
        <div className="bg-surface rounded-sm shadow-sm p-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full mb-2" />
          ))}
        </div>
      ) : (
        <DataGrid<PropertyValuation>
          data={filtered}
          columns={columns}
          enablePagination
          defaultPageSize={25}
          hoverable
          getRowId={(row) => row.valuationId}
          emptyState={
            <CollateralListEmptyState
              copy={VALUATION_EMPTY_COPY}
              readFailed={!!error}
              filteredOut={!error && valuations.length > 0}
            />
          }
          className="w-full rounded-b-sm shadow-sm"
        />
      )}

      {/* New Valuation Dialog */}
      <Dialog
        open={newValuationDialogOpen}
        onOpenChange={(open) => { if (!open) setNewValuationDialogOpen(false); }}
        title="Request New Property Valuation"
        size="md"
        footer={
          <>
            <Button
              variant="tertiary"
              onClick={() => setNewValuationDialogOpen(false)}
              disabled={createValuation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              icon={PropertyIcon}
              iconPosition="left"
              onClick={handleSubmitNewValuation}
              disabled={createValuation.isPending}
            >
              {createValuation.isPending ? 'Requesting...' : 'Request Valuation'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <BodyText variant="ui" className="text-tertiary text-sm">
            Enter the property address to initiate a valuation request through the Reference Model.
          </BodyText>

          {newValuationError && (
            <Alert type="error">{newValuationError}</Alert>
          )}

          <div>
            <label htmlFor="valuation-street" className="block text-sm font-medium text-secondary mb-1">
              Street Address <span className="text-error">*</span>
            </label>
            <input
              id="valuation-street"
              type="text"
              value={newValuationForm.street}
              onChange={(e) => setNewValuationForm(prev => ({ ...prev, street: e.target.value }))}
              placeholder="123 Main Street"
              className={INPUT_CLASS}
              disabled={createValuation.isPending}
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="valuation-city" className="block text-sm font-medium text-secondary mb-1">
                City <span className="text-error">*</span>
              </label>
              <input
                id="valuation-city"
                type="text"
                value={newValuationForm.city}
                onChange={(e) => setNewValuationForm(prev => ({ ...prev, city: e.target.value }))}
                placeholder="Springfield"
                className={INPUT_CLASS}
                disabled={createValuation.isPending}
                required
              />
            </div>
            <div>
              <label htmlFor="valuation-state" className="block text-sm font-medium text-secondary mb-1">
                State <span className="text-error">*</span>
              </label>
              <input
                id="valuation-state"
                type="text"
                value={newValuationForm.state}
                onChange={(e) => setNewValuationForm(prev => ({ ...prev, state: e.target.value.toUpperCase() }))}
                placeholder="IL"
                maxLength={2}
                className={INPUT_CLASS}
                disabled={createValuation.isPending}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="valuation-zip" className="block text-sm font-medium text-secondary mb-1">
                ZIP Code <span className="text-error">*</span>
              </label>
              <input
                id="valuation-zip"
                type="text"
                inputMode="numeric"
                value={newValuationForm.zipCode}
                onChange={(e) => setNewValuationForm(prev => ({ ...prev, zipCode: e.target.value }))}
                placeholder="62701"
                maxLength={10}
                className={INPUT_CLASS}
                disabled={createValuation.isPending}
                required
              />
            </div>
            <div>
              <label htmlFor="valuation-property-type" className="block text-sm font-medium text-secondary mb-1">
                Property Type
              </label>
              <select
                id="valuation-property-type"
                value={newValuationForm.propertyType}
                onChange={(e) => setNewValuationForm(prev => ({ ...prev, propertyType: e.target.value }))}
                className={INPUT_CLASS}
                disabled={createValuation.isPending}
              >
                <option value="">Select type</option>
                <option value="SINGLE_FAMILY">Single Family</option>
                <option value="MULTI_FAMILY">Multi Family</option>
                <option value="CONDO">Condo</option>
                <option value="COMMERCIAL">Commercial</option>
                <option value="LAND">Land</option>
              </select>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog
        open={!!selectedValuation}
        onOpenChange={(open) => { if (!open) setSelectedValuation(null); }}
        title="Property Valuation Details"
        size="lg"
        footer={
          <>
            <Button variant="tertiary" onClick={() => setSelectedValuation(null)}>
              Close
            </Button>
            {(selectedValuation?.status === 'NEEDS_REVIEW' || selectedValuation?.status === 'FAILED') && (
              <Button
                variant="primary"
                icon={RefreshIcon}
                iconPosition="left"
                onClick={() => selectedValuation && handleRevalue(selectedValuation)}
                disabled={createValuation.isPending}
              >
                {createValuation.isPending
                  ? 'Requesting...'
                  : selectedValuation.status === 'FAILED'
                    ? 'Retry Valuation'
                    : 'Revalue'}
              </Button>
            )}
          </>
        }
      >
        {selectedValuation && (
          <>
            {revalueError && (
              <Alert type="error" className="mb-4">{revalueError}</Alert>
            )}

            {selectedValuation.status === 'NEEDS_REVIEW' && (
              <Card className="p-4 mb-4 bg-warning/10 border border-warning/20">
                <div className="flex items-start gap-2">
                  <WarningIcon className="text-warning flex-shrink-0" size={20} />
                  <BodyText variant="ui" className="text-warning">
                    This valuation requires manual review due to low confidence or source divergence.
                    Revalue requests a fresh valuation for the same address and replaces this one.
                  </BodyText>
                </div>
              </Card>
            )}

            {selectedValuation.status === 'FAILED' && (
              <Card className="p-4 mb-4 bg-error/10 border border-error/20">
                <BodyText variant="ui" className="text-error">
                  Valuation failed. No providers returned data for this property.
                </BodyText>
              </Card>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <BodyText variant="ui" className="text-xs text-tertiary">Property Address</BodyText>
                <BodyText variant="ui" className="font-medium">
                  {[selectedValuation.address?.street, addressLine(selectedValuation)].filter(Boolean).join(', ')}
                </BodyText>
              </div>
              <div>
                <BodyText variant="ui" className="text-xs text-tertiary">Certified Value</BodyText>
                <BodyText variant="ui" className="font-medium text-lg">
                  {certifiedFigure(selectedValuation) !== undefined
                    ? formatMoney(certifiedFigure(selectedValuation))
                    : 'Not available'}
                </BodyText>
              </div>
              <div>
                <BodyText variant="ui" className="text-xs text-tertiary">Confidence Score</BodyText>
                {isMeasured(selectedValuation.confidenceScore) && selectedValuation.confidenceScore > 0 ? (
                  <BodyText
                    variant="ui"
                    className={`font-medium text-lg ${confidenceColor(selectedValuation.confidenceScore)}`}
                  >
                    {formatRatioAsPercent(selectedValuation.confidenceScore)}
                  </BodyText>
                ) : (
                  <BodyText variant="ui" className="text-tertiary">Not available</BodyText>
                )}
              </div>
              <div>
                <BodyText variant="ui" className="text-xs text-tertiary">Status</BodyText>
                <ValuationStatusBadge status={selectedValuation.status} />
              </div>
              <div>
                <BodyText variant="ui" className="text-xs text-tertiary">Valuation Date</BodyText>
                <BodyText variant="ui">{formatDateTime(selectedValuation.valuationDate)}</BodyText>
              </div>
              <div>
                <BodyText variant="ui" className="text-xs text-tertiary">Data Sources</BodyText>
                <div className="flex gap-1 mt-1">
                  {selectedValuation.sources && selectedValuation.sources.length > 0 ? (
                    selectedValuation.sources.map((source) => (
                      <Badge key={source} variant="neutral">
                        {source}
                      </Badge>
                    ))
                  ) : (
                    <BodyText variant="ui" className="text-tertiary">None</BodyText>
                  )}
                </div>
              </div>
              <div>
                <BodyText variant="ui" className="text-xs text-tertiary">Valuation ID</BodyText>
                <BodyText variant="ui" className="font-mono text-sm">{selectedValuation.valuationId}</BodyText>
              </div>
              <div>
                <BodyText variant="ui" className="text-xs text-tertiary">Tenant and Loan</BodyText>
                <TenantLoanCell
                  tenantId={selectedValuation.tenantId}
                  loanId={selectedValuation.loanId}
                  names={tenantNames}
                />
              </div>
            </div>
          </>
        )}
      </Dialog>
    </div>
  );
}
